if (!customElements.get('system-addons')) {
  class SystemAddons extends HTMLElement {
    constructor() {
      super();
      
      this.addEventListener('click', this.onClick.bind(this));
      
      // Subscribe to variant change - same pattern as BundleSave in global.js
      window.FoxThemeEvents.subscribe(PUB_SUB_EVENTS.variantChange, (evt) => {
        if (evt.data.sectionId === this.dataset.sectionId) {
          this.onMainVariantChange(evt.data.variant);
        }
      });
    }

    connectedCallback() {
      this.init();
    }

    init() {
      const mainProductInfo = document.querySelector(`product-info[id^='MainProduct-${this.dataset.sectionId}']`);
      if (!mainProductInfo) return;
      
      const currentVariantId = mainProductInfo.querySelector('input[name="id"]')?.value;
      
      // Try productData property first (set by product-info.js), fall back to script tag
      let productData = mainProductInfo.productData;
      if (!productData) {
        const productDataScript = mainProductInfo.querySelector('#ProductData');
        if (productDataScript) {
          try { productData = JSON.parse(productDataScript.textContent); } catch(e) {}
        }
      }
      
      if (currentVariantId && productData) {
        const variant = productData.variants.find(v => v.id == currentVariantId);
        if (variant) this.onMainVariantChange(variant);
      }
    }

    onMainVariantChange(variant) {
      if (!variant) return;
      
      const mainProductInfo = document.querySelector(`product-info[id^='MainProduct-${this.dataset.sectionId}']`);
      if (!mainProductInfo) return;
      
      // Use productData property (same as BundleSave)
      const productData = mainProductInfo.productData;
      if (!productData) return;
      
      let sizeIndex = -1;
      for (let i = 0; i < productData.options.length; i++) {
        if (productData.options[i].name.toLowerCase().includes('size')) {
          sizeIndex = i;
          break;
        }
      }
      
      if (sizeIndex === -1) return;
      
      const currentSize = variant.options[sizeIndex];
      this.updateItemsSize(currentSize);
    }

    updateItemsSize(size) {
      if (!size) return;
      
      const normalizeJSSize = (str) => {
        const s = str.trim().toLowerCase();
        if (s === 'medium' || s === 'med') return 'medium';
        if (s === 'extra large' || s === 'xl') return 'extra large';
        return s;
      };

      const normalizedSize = normalizeJSSize(size);
      const items = this.querySelectorAll('[data-bundle-item]');
      
      items.forEach(item => {
        let shouldHide = false;

        // Filter by meta sizes if present
        if (item.dataset.metaSizes) {
          const metaSizes = item.dataset.metaSizes.split(',').map(s => normalizeJSSize(s));
          if (!metaSizes.includes(normalizedSize)) {
            shouldHide = true;
          }
        }

        if (!shouldHide) {
          if (item.dataset.hasSize === 'false') {
            shouldHide = false;
          } else {
            const itemSizeIndex = parseInt(item.dataset.sizeIndex);
            const variantsScript = item.querySelector('[data-item-variants]');
            if (variantsScript) {
              const variants = JSON.parse(variantsScript.textContent);
              const matchingVariant = variants.find(v => {
                 const vSize = v.options ? v.options[itemSizeIndex] : v[`option${itemSizeIndex + 1}`];
                 if (!vSize) return false;
                 
                 const vSizeLower = vSize.trim().toLowerCase();
                 const vSizeNormalized = normalizeJSSize(vSizeLower);
                 
                 if (vSizeNormalized === normalizedSize || vSizeNormalized.includes(normalizedSize)) return true;
                 
                 // Synonym check for substring matching
                 if (normalizedSize === 'extra large') {
                   if (vSizeLower.includes('xl') || vSizeLower.includes('extra large') || vSizeLower.includes('extra-large')) return true;
                 }
                 if (normalizedSize === 'medium') {
                   if (vSizeLower.includes('med') || vSizeLower.includes('medium')) return true;
                 }
                 
                 return false;
              });
              
              if (matchingVariant) {
                this.updateItemUI(item, matchingVariant);
              } else {
                shouldHide = true;
              }
            }
          }
        }

        if (shouldHide) {
          item.classList.add('hidden', 'f-hidden');
          item.style.setProperty('display', 'none', 'important');
        } else {
          item.classList.remove('hidden', 'f-hidden');
          item.style.removeProperty('display');
        }
      });
    }

    updateItemUI(item, variant) {
      const atcButton = item.querySelector('[data-add-to-cart]');
      const imageContainer = item.querySelector('.system-addons__item-media img');

      if (atcButton) {
        atcButton.dataset.variantId = variant.id;
        
        // Format price and update
        let formattedPrice = '';
        if (typeof formatMoney === 'function' && window.FoxThemeSettings) {
          const moneyFormat = window.FoxThemeSettings.money_format;
          formattedPrice = formatMoney(variant.price, moneyFormat);
        } else {
          // fallback if formatMoney is not available
          formattedPrice = `$${(variant.price / 100).toFixed(2)}`;
        }
        
        atcButton.dataset.price = formattedPrice;
        
        const actionSpan = atcButton.querySelector('.btn-text-action');
        const priceSpan = atcButton.querySelector('.btn-text-price');
        const btnTextSpan = atcButton.querySelector('.btn-text');

        if (variant.available) {
          atcButton.removeAttribute('disabled');
          if (actionSpan && priceSpan) {
            actionSpan.textContent = 'Add';
            priceSpan.innerHTML = formattedPrice;
          } else if (btnTextSpan) {
            btnTextSpan.innerHTML = `Add &bull; ${formattedPrice}`;
          }
        } else {
          atcButton.setAttribute('disabled', 'disabled');
          if (actionSpan) {
            actionSpan.textContent = 'Sold Out';
          }
          if (priceSpan) {
            priceSpan.innerHTML = '';
          }
          if (btnTextSpan && !actionSpan) {
            btnTextSpan.textContent = 'Sold Out';
          }
        }
      }
      
      if (variant.featured_image && imageContainer) {
        imageContainer.src = variant.featured_image.src;
        imageContainer.srcset = '';
      }
    }

    async onClick(event) {
      const target = event.target.closest('[data-add-to-cart]');
      if (!target) return;

      event.preventDefault();
      
      if (target.classList.contains('btn--loading')) return;
      
      const variantId = target.dataset.variantId;
      if (!variantId) return;

      target.setAttribute('aria-disabled', 'true');
      
      const btnText = target.querySelector('.btn-text');

      const formData = {
        'items': [{
          'id': variantId,
          'quantity': 1
        }],
        'sections': ['cart-drawer']
      };

      try {
        const response = await fetch(window.FoxThemeSettings?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        const data = await response.json();
        
        if (data.status) {
          throw new Error(data.description || data.message);
        }

        const cartUpdateEvent = (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) ? PUB_SUB_EVENTS.cartUpdate : 'cart-update';
        if (window.FoxThemeEvents) {
          window.FoxThemeEvents.emit(cartUpdateEvent, data);
        }
        
        // Show success state
        if (btnText) {
          const actionSpan = btnText.querySelector('.btn-text-action');
          if (actionSpan) {
            actionSpan.textContent = 'Added';
            const priceSpan = btnText.querySelector('.btn-text-price');
            const sepSpan = btnText.querySelector('.btn-text-separator');
            if (priceSpan) priceSpan.style.display = 'none';
            if (sepSpan) sepSpan.style.display = 'none';
          } else {
            btnText.textContent = 'Added';
          }
        }
        
        setTimeout(() => {
          if (btnText) {
            const actionSpan = btnText.querySelector('.btn-text-action');
            if (actionSpan) {
              actionSpan.textContent = 'Add';
              const priceSpan = btnText.querySelector('.btn-text-price');
              const sepSpan = btnText.querySelector('.btn-text-separator');
              if (priceSpan) priceSpan.style.display = '';
              if (sepSpan) sepSpan.style.display = '';
            } else {
              const originalPrice = target.dataset.price || '';
              btnText.innerHTML = originalPrice ? `Add &bull; ${originalPrice}` : 'Add';
            }
          }
        }, 2000);

      } catch (error) {
        console.error('SystemAddons Error:', error);
        
        if (window.FoxTheme?.Notification) {
          window.FoxTheme.Notification.show({
            target: document.body,
            method: 'appendChild',
            type: 'warning',
            message: error.message || 'Failed to add item to cart',
            last: 3000
          });
        } else {
          alert(error.message || 'Failed to add item to cart');
        }
      } finally {
        target.removeAttribute('aria-disabled');
      }
    }
  }

  customElements.define('system-addons', SystemAddons);
}